import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { useWorkspace } from '../contexts/WorkspaceContext';
import {
  Video, Wand2, Play, Download, Settings, Sparkles, Clock,
  Film, Mic, Type, Image, RefreshCw, ChevronRight, Plus,
  Edit2, Trash2, Eye, Upload, Check, X, Loader2, Volume2,
  User, Users, Palette, Camera, Globe, Zap, Save, Copy,
  Star, Heart, Shirt, Glasses, MapPin
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== CRÉATEUR D'INFLUENCEURS IA ====================

const CreateurInfluenceur = ({ config, onSave, onCancel, societeId }) => {
  const [influenceur, setInfluenceur] = useState({
    nom: '',
    genre: 'femme',
    age: '25-35',
    ethnicite: 'europeen',
    style_vestimentaire: 'agriculteur_moderne',
    cheveux: 'brun',
    physique: 'normal',
    accessoires: [],
    environnement: 'champs_ble',
    expression: 'souriant',
    voix: 'feminine_fr',
    ton_parole: 'chaleureux',
    description_custom: ''
  });
  
  const [previewPrompt, setPreviewPrompt] = useState('');
  const [saving, setSaving] = useState(false);

  const options = config?.options_influenceurs || {};

  // Générer le prompt de preview
  useEffect(() => {
    generatePreviewPrompt();
  }, [influenceur]);

  const generatePreviewPrompt = () => {
    const parts = [];
    
    // Genre et âge
    const genre = influenceur.genre === 'femme' ? 'French woman' : influenceur.genre === 'homme' ? 'French man' : 'French person';
    const ageOpt = options.ages?.find(a => a.id === influenceur.age);
    parts.push(`${genre}, ${ageOpt?.prompt || 'adult'}`);
    
    // Ethnicité
    const ethOpt = options.ethnicites?.find(e => e.id === influenceur.ethnicite);
    if (ethOpt?.prompt) parts.push(ethOpt.prompt);
    
    // Physique et cheveux
    const physOpt = options.physiques?.find(p => p.id === influenceur.physique);
    if (physOpt?.prompt) parts.push(physOpt.prompt);
    
    const chevOpt = options.cheveux?.find(c => c.id === influenceur.cheveux);
    if (chevOpt?.prompt) parts.push(chevOpt.prompt);
    
    // Style vestimentaire
    const styleOpt = options.styles_vestimentaires?.find(s => s.id === influenceur.style_vestimentaire);
    if (styleOpt?.prompt) parts.push(styleOpt.prompt);
    
    // Accessoires
    influenceur.accessoires.forEach(accId => {
      const accOpt = options.accessoires?.find(a => a.id === accId);
      if (accOpt?.prompt) parts.push(accOpt.prompt);
    });
    
    // Expression
    const exprOpt = options.expressions?.find(e => e.id === influenceur.expression);
    if (exprOpt?.prompt) parts.push(exprOpt.prompt);
    
    // Environnement
    const envOpt = options.environnements?.find(e => e.id === influenceur.environnement);
    if (envOpt?.prompt) parts.push(envOpt.prompt);
    
    // Description custom
    if (influenceur.description_custom) {
      parts.push(influenceur.description_custom);
    }
    
    parts.push('professional photography, 4K, cinematic lighting');
    
    setPreviewPrompt(parts.join(', '));
  };

  const toggleAccessoire = (accId) => {
    setInfluenceur(prev => ({
      ...prev,
      accessoires: prev.accessoires.includes(accId)
        ? prev.accessoires.filter(a => a !== accId)
        : [...prev.accessoires, accId]
    }));
  };

  const handleSave = async () => {
    if (!influenceur.nom) {
      toast.error('Donnez un nom à votre influenceur');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/studio-ia/influenceurs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': societeId || ''
        },
        body: JSON.stringify({
          ...influenceur,
          prompt_genere: previewPrompt
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Influenceur "${influenceur.nom}" créé !`);
        onSave?.(data);
      } else {
        throw new Error('Erreur');
      }
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-gradient-to-br from-pink-500 to-purple-500 rounded-xl">
          <User className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold">Créer votre Influenceur IA</h2>
          <p className="text-sm text-muted-foreground">Personnalisez chaque détail</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Colonne gauche - Configuration */}
        <div className="space-y-4">
          {/* Nom */}
          <div>
            <Label>Nom de l'influenceur *</Label>
            <Input
              value={influenceur.nom}
              onChange={e => setInfluenceur(p => ({ ...p, nom: e.target.value }))}
              placeholder="Ex: Marie, Pierre, Alex..."
              className="mt-1"
            />
          </div>

          {/* Genre et Âge */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Genre</Label>
              <div className="flex gap-2 mt-2">
                {options.genres?.map(g => (
                  <button
                    key={g.id}
                    onClick={() => setInfluenceur(p => ({ ...p, genre: g.id }))}
                    className={`flex-1 p-3 rounded-lg border-2 text-center transition-all ${
                      influenceur.genre === g.id 
                        ? 'border-pink-500 bg-pink-50' 
                        : 'border-gray-200 hover:border-pink-300'
                    }`}
                  >
                    <span className="text-2xl">{g.icone}</span>
                    <p className="text-xs mt-1">{g.nom}</p>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Tranche d'âge</Label>
              <Select value={influenceur.age} onValueChange={v => setInfluenceur(p => ({ ...p, age: v }))}>
                <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {options.ages?.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Apparence */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Ethnicité</Label>
              <Select value={influenceur.ethnicite} onValueChange={v => setInfluenceur(p => ({ ...p, ethnicite: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {options.ethnicites?.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Physique</Label>
              <Select value={influenceur.physique} onValueChange={v => setInfluenceur(p => ({ ...p, physique: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {options.physiques?.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Cheveux */}
          <div>
            <Label className="flex items-center gap-2"><Palette className="w-4 h-4" /> Cheveux</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {options.cheveux?.map(c => (
                <button
                  key={c.id}
                  onClick={() => setInfluenceur(p => ({ ...p, cheveux: c.id }))}
                  className={`px-3 py-1.5 rounded-full border-2 text-sm transition-all ${
                    influenceur.cheveux === c.id 
                      ? 'border-pink-500 bg-pink-50 text-pink-700' 
                      : 'border-gray-200 hover:border-pink-300'
                  }`}
                >
                  {c.nom}
                </button>
              ))}
            </div>
          </div>

          {/* Style vestimentaire */}
          <div>
            <Label className="flex items-center gap-2"><Shirt className="w-4 h-4" /> Style vestimentaire</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {options.styles_vestimentaires?.map(s => (
                <button
                  key={s.id}
                  onClick={() => setInfluenceur(p => ({ ...p, style_vestimentaire: s.id }))}
                  className={`p-2 rounded-lg border-2 text-left transition-all ${
                    influenceur.style_vestimentaire === s.id 
                      ? 'border-pink-500 bg-pink-50' 
                      : 'border-gray-200 hover:border-pink-300'
                  }`}
                >
                  <span className="text-lg">{s.icone}</span>
                  <p className="text-xs font-medium mt-1">{s.nom}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Accessoires */}
          <div>
            <Label className="flex items-center gap-2"><Glasses className="w-4 h-4" /> Accessoires</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {options.accessoires?.map(a => (
                <button
                  key={a.id}
                  onClick={() => toggleAccessoire(a.id)}
                  className={`px-3 py-1.5 rounded-full border-2 text-xs transition-all ${
                    influenceur.accessoires.includes(a.id)
                      ? 'border-pink-500 bg-pink-500 text-white' 
                      : 'border-gray-200 hover:border-pink-300'
                  }`}
                >
                  {a.nom}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Colonne droite - Environnement, Expression, Voix */}
        <div className="space-y-4">
          {/* Environnement */}
          <div>
            <Label className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Environnement</Label>
            <div className="grid grid-cols-3 gap-2 mt-2 max-h-48 overflow-y-auto">
              {options.environnements?.map(e => (
                <button
                  key={e.id}
                  onClick={() => setInfluenceur(p => ({ ...p, environnement: e.id }))}
                  className={`p-2 rounded-lg border-2 text-center transition-all ${
                    influenceur.environnement === e.id 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <span className="text-xl">{e.icone}</span>
                  <p className="text-xs mt-1">{e.nom}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Expression */}
          <div>
            <Label className="flex items-center gap-2"><Heart className="w-4 h-4" /> Expression</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {options.expressions?.map(e => (
                <button
                  key={e.id}
                  onClick={() => setInfluenceur(p => ({ ...p, expression: e.id }))}
                  className={`px-3 py-1.5 rounded-full border-2 text-sm transition-all ${
                    influenceur.expression === e.id 
                      ? 'border-amber-500 bg-amber-50 text-amber-700' 
                      : 'border-gray-200 hover:border-amber-300'
                  }`}
                >
                  {e.nom}
                </button>
              ))}
            </div>
          </div>

          {/* Voix et Ton */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-2"><Mic className="w-4 h-4" /> Voix</Label>
              <Select value={influenceur.voix} onValueChange={v => setInfluenceur(p => ({ ...p, voix: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {config?.voix?.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.icone} {v.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ton de parole</Label>
              <Select value={influenceur.ton_parole} onValueChange={v => setInfluenceur(p => ({ ...p, ton_parole: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {options.tons_parole?.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Description personnalisée */}
          <div>
            <Label className="flex items-center gap-2">
              <Mic className="w-4 h-4" /> Détails supplémentaires (optionnel)
            </Label>
            <div className="relative">
              <Textarea
                value={influenceur.description_custom}
                onChange={e => setInfluenceur(p => ({ ...p, description_custom: e.target.value }))}
                placeholder="Décrivez votre influenceur idéal... Ex: 'Un agriculteur passionné de 35 ans avec un accent du sud'"
                rows={3}
                className="mt-1 pr-12"
              />
              <VoiceInputButton 
                onTranscript={(text) => setInfluenceur(p => ({ 
                  ...p, 
                  description_custom: p.description_custom ? p.description_custom + ' ' + text : text 
                }))}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              💡 Cliquez sur le micro pour dicter votre description
            </p>
          </div>

          {/* Preview du prompt */}
          <Card className="bg-gradient-to-r from-purple-50 to-pink-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Eye className="w-4 h-4" /> Aperçu du prompt IA
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground italic leading-relaxed">
                {previewPrompt || 'Configurez votre influenceur pour voir le prompt généré'}
              </p>
            </CardContent>
          </Card>

          {/* Boutons */}
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={onCancel} className="flex-1">
              Annuler
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving || !influenceur.nom}
              className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Sauvegarder
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== CARTE INFLUENCEUR ====================

const InfluenceurCard = ({ influenceur, onSelect, selected }) => (
  <button
    onClick={() => onSelect(influenceur)}
    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
      selected 
        ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200' 
        : 'border-gray-200 hover:border-purple-300 hover:shadow-md'
    }`}
  >
    <div className="flex items-center gap-3">
      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${
        influenceur.genre === 'femme' ? 'bg-pink-100' : influenceur.genre === 'homme' ? 'bg-blue-100' : 'bg-purple-100'
      }`}>
        {influenceur.icone || (influenceur.genre === 'femme' ? '👩' : influenceur.genre === 'homme' ? '👨' : '🧑')}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold truncate">{influenceur.nom}</p>
        <p className="text-xs text-muted-foreground truncate">{influenceur.description}</p>
      </div>
      {selected && <Check className="w-5 h-5 text-purple-500 flex-shrink-0" />}
    </div>
    <div className="flex flex-wrap gap-1 mt-2">
      <Badge variant="secondary" className="text-xs">{influenceur.age}</Badge>
      <Badge variant="secondary" className="text-xs">{influenceur.environnement}</Badge>
    </div>
  </button>
);

// ==================== COMPOSANT DICTÉE VOCALE ====================

const VoiceInputButton = ({ onTranscript }) => {
  const [isListening, setIsListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const recognitionRef = useRef(null);

  useEffect(() => {
    // Vérifier si la Web Speech API est supportée
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setSupported(false);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = 'fr-FR';

    recognitionRef.current.onresult = (event) => {
      let finalTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        }
      }
      if (finalTranscript) {
        onTranscript(finalTranscript);
      }
    };

    recognitionRef.current.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
    };

    recognitionRef.current.onend = () => {
      setIsListening(false);
    };

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [onTranscript]);

  const toggleListening = () => {
    if (!supported) {
      toast.error('La dictée vocale n\'est pas supportée par votre navigateur');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current?.start();
        setIsListening(true);
        toast.success('Parlez maintenant...');
      } catch (error) {
        console.error('Error starting recognition:', error);
      }
    }
  };

  if (!supported) return null;

  return (
    <button
      type="button"
      onClick={toggleListening}
      className={`absolute right-2 top-2 p-2 rounded-full transition-all ${
        isListening 
          ? 'bg-red-500 text-white animate-pulse' 
          : 'bg-gray-100 hover:bg-purple-100 text-gray-600 hover:text-purple-600'
      }`}
      title={isListening ? 'Arrêter la dictée' : 'Dicter votre texte'}
    >
      <Mic className={`w-5 h-5 ${isListening ? 'animate-pulse' : ''}`} />
    </button>
  );
};

// ==================== PAGE PRINCIPALE ====================

const StudioIAMarketingPage = () => {
  const { activeSociete } = useWorkspace();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projets, setProjets] = useState([]);
  const [mesInfluenceurs, setMesInfluenceurs] = useState([]);
  
  // Navigation
  const [activeTab, setActiveTab] = useState('creer'); // creer, influenceurs, projets
  const [step, setStep] = useState(1);
  const [showCreateInfluenceur, setShowCreateInfluenceur] = useState(false);
  
  // Formulaire vidéo
  const [formData, setFormData] = useState({
    theme: 'bureau_agricole',
    objectif: 'promotion',
    produit_service: 'Bureau Agricole',
    ton: 'professionnel',
    duree_totale: 36,
    format: 'vertical_9_16',
    voix: 'feminine_fr',
    avec_soustitres: true,
    message_cle: '',
    call_to_action: 'Inscrivez-vous gratuitement sur bureauagricole.fr',
    influenceur_id: null
  });
  
  const [script, setScript] = useState(null);
  const [projetActif, setProjetActif] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatingScript, setGeneratingScript] = useState(false);

  // Headers avec societe_id pour cloisonnement
  const getHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
    'X-Societe-Id': activeSociete?.id || ''
  });

  useEffect(() => {
    if (activeSociete?.id) {
      loadData();
    }
  }, [activeSociete?.id]);

  useEffect(() => {
    let interval;
    if (projetActif?.statut === 'en_cours') {
      interval = setInterval(() => refreshProjet(projetActif.id), 5000);
    }
    return () => clearInterval(interval);
  }, [projetActif?.statut]);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = getHeaders();
      
      const [configRes, projetsRes, influenceursRes] = await Promise.all([
        fetch(`${API_URL}/api/studio-ia/config`, { headers }),
        fetch(`${API_URL}/api/studio-ia/projets`, { headers }),
        fetch(`${API_URL}/api/studio-ia/influenceurs`, { headers })
      ]);
      
      if (configRes.ok) setConfig(await configRes.json());
      if (projetsRes.ok) {
        const data = await projetsRes.json();
        setProjets(data.projets || []);
      }
      if (influenceursRes.ok) {
        const data = await influenceursRes.json();
        setMesInfluenceurs(data.influenceurs || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const refreshProjet = async (projetId) => {
    try {
      const res = await fetch(`${API_URL}/api/studio-ia/projet/${projetId}`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const projet = await res.json();
        setProjetActif(projet);
        if (projet.statut === 'termine') {
          toast.success('Vidéo générée avec succès !');
          loadData();
        } else if (projet.statut === 'erreur') {
          toast.error('Erreur lors de la génération');
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const genererScript = async () => {
    setGeneratingScript(true);
    try {
      const res = await fetch(`${API_URL}/api/studio-ia/generer-script`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        const data = await res.json();
        setScript(data);
        setStep(2);
        toast.success('Script généré !');
      } else {
        throw new Error('Erreur');
      }
    } catch (err) {
      toast.error('Erreur lors de la génération du script');
    } finally {
      setGeneratingScript(false);
    }
  };

  const creerProjetEtGenerer = async () => {
    setGenerating(true);
    try {
      const headers = getHeaders();
      
      // Créer le projet
      const resProjet = await fetch(`${API_URL}/api/studio-ia/creer-projet`, {
        method: 'POST',
        headers,
        body: JSON.stringify(formData)
      });
      
      if (!resProjet.ok) throw new Error('Erreur création projet');
      const { projet_id } = await resProjet.json();
      
      // Sauvegarder le script
      await fetch(`${API_URL}/api/studio-ia/projet/${projet_id}/script`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ projet_id, script: script.scenes })
      });
      
      // Lancer la génération
      const resGen = await fetch(`${API_URL}/api/studio-ia/generer-video/${projet_id}`, {
        method: 'POST',
        headers
      });
      
      if (resGen.ok) {
        setProjetActif({ id: projet_id, statut: 'en_cours', progression: 5 });
        setStep(3);
        toast.success('Génération lancée !');
      }
    } catch (err) {
      toast.error('Erreur lors de la création');
    } finally {
      setGenerating(false);
    }
  };

  const updateScene = (index, field, value) => {
    const newScenes = [...script.scenes];
    newScenes[index] = { ...newScenes[index], [field]: value };
    setScript({ ...script, scenes: newScenes });
  };

  const selectInfluenceur = (inf) => {
    setFormData(f => ({ 
      ...f, 
      influenceur_id: inf.id,
      voix: inf.voix || f.voix
    }));
  };

  const onInfluenceurCreated = (newInf) => {
    setMesInfluenceurs(prev => [...prev, newInf]);
    setShowCreateInfluenceur(false);
    selectInfluenceur(newInf);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 p-4 md:p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl shadow-lg">
              <Video className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Studio IA Marketing
              </h1>
              <p className="text-muted-foreground">Créez vos Réels et influenceurs avec l'IA</p>
            </div>
          </div>
          
          <Button 
            onClick={() => setShowCreateInfluenceur(true)}
            className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
          >
            <Plus className="w-4 h-4 mr-2" /> Créer un influenceur
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate('/agents/config', { state: { selectedAgent: 'marketing' } })}
            data-testid="agent-settings-btn"
          >
            <Settings className="w-4 h-4 mr-2" /> Paramètres
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="creer" className="gap-2">
              <Video className="w-4 h-4" /> Créer une vidéo
            </TabsTrigger>
            <TabsTrigger value="influenceurs" className="gap-2">
              <Users className="w-4 h-4" /> Mes influenceurs
              {mesInfluenceurs.length > 0 && (
                <Badge variant="secondary" className="ml-1">{mesInfluenceurs.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="projets" className="gap-2">
              <Film className="w-4 h-4" /> Mes vidéos
              {projets.length > 0 && (
                <Badge variant="secondary" className="ml-1">{projets.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* TAB: Créer une vidéo */}
          <TabsContent value="creer">
            {/* Stepper */}
            <div className="flex items-center gap-2 mb-6">
              {[
                { num: 1, label: 'Configuration' },
                { num: 2, label: 'Script' },
                { num: 3, label: 'Génération' }
              ].map((s, i) => (
                <div key={s.num} className="flex items-center">
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all
                    ${step >= s.num ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'}`}>
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm
                      ${step >= s.num ? 'bg-purple-500 text-white' : 'bg-gray-300 text-gray-600'}`}>
                      {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                    </span>
                    <span className="hidden sm:inline">{s.label}</span>
                  </div>
                  {i < 2 && <ChevronRight className="w-4 h-4 text-gray-300 mx-2" />}
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              {/* Panneau principal */}
              <div className="lg:col-span-2">
                {step === 1 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Settings className="w-5 h-5" /> Configuration
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Sélection Influenceur */}
                      <div>
                        <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                          <User className="w-4 h-4" /> Influenceur IA
                        </Label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-64 overflow-y-auto p-1">
                          {/* Influenceurs prédéfinis */}
                          {config?.influenceurs_predefinis?.map(inf => (
                            <InfluenceurCard
                              key={inf.id}
                              influenceur={inf}
                              selected={formData.influenceur_id === inf.id}
                              onSelect={selectInfluenceur}
                            />
                          ))}
                          {/* Mes influenceurs */}
                          {mesInfluenceurs.map(inf => (
                            <InfluenceurCard
                              key={inf.id}
                              influenceur={inf}
                              selected={formData.influenceur_id === inf.id}
                              onSelect={selectInfluenceur}
                            />
                          ))}
                          {/* Bouton créer */}
                          <button
                            onClick={() => setShowCreateInfluenceur(true)}
                            className="p-4 rounded-xl border-2 border-dashed border-purple-300 text-center hover:border-purple-500 hover:bg-purple-50 transition-all"
                          >
                            <Plus className="w-8 h-8 text-purple-400 mx-auto" />
                            <p className="text-sm text-purple-600 mt-2">Créer le vôtre</p>
                          </button>
                        </div>
                      </div>

                      <Separator />

                      {/* Thème */}
                      <div>
                        <Label className="text-sm font-medium mb-2 block">Thème</Label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {config?.themes?.map(t => (
                            <button
                              key={t.id}
                              onClick={() => setFormData(f => ({ ...f, theme: t.id }))}
                              className={`p-3 rounded-lg border-2 text-left transition-all
                                ${formData.theme === t.id ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-200'}`}
                            >
                              <span className="text-xl">{t.icone}</span>
                              <p className="font-medium text-sm mt-1">{t.nom}</p>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Format & Durée */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label>Format</Label>
                          <Select value={formData.format} onValueChange={v => setFormData(f => ({ ...f, format: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {config?.formats?.map(f => (
                                <SelectItem key={f.id} value={f.id}>{f.icone} {f.nom}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Durée</Label>
                          <Select value={String(formData.duree_totale)} onValueChange={v => setFormData(f => ({ ...f, duree_totale: parseInt(v) }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {config?.durees_disponibles?.map(d => (
                                <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Objectif & Ton */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label>Objectif</Label>
                          <Select value={formData.objectif} onValueChange={v => setFormData(f => ({ ...f, objectif: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {config?.objectifs_disponibles?.map(o => (
                                <SelectItem key={o.id} value={o.id}>{o.icone} {o.nom}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Ton</Label>
                          <Select value={formData.ton} onValueChange={v => setFormData(f => ({ ...f, ton: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {config?.tons_disponibles?.map(t => (
                                <SelectItem key={t.id} value={t.id}>{t.icone} {t.nom}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Produit & CTA */}
                      <div className="space-y-4">
                        <div>
                          <Label>Produit / Service</Label>
                          <Input 
                            value={formData.produit_service}
                            onChange={e => setFormData(f => ({ ...f, produit_service: e.target.value }))}
                            placeholder="Ex: Bureau Agricole, Ma ferme..."
                          />
                        </div>
                        <div>
                          <Label>Call to action</Label>
                          <Input 
                            value={formData.call_to_action}
                            onChange={e => setFormData(f => ({ ...f, call_to_action: e.target.value }))}
                            placeholder="Ex: Inscrivez-vous sur bureauagricole.fr"
                          />
                        </div>
                      </div>

                      {/* Sous-titres */}
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <Label>Sous-titres automatiques</Label>
                          <p className="text-xs text-muted-foreground">Ajoute le texte à l'écran</p>
                        </div>
                        <Switch 
                          checked={formData.avec_soustitres}
                          onCheckedChange={c => setFormData(f => ({ ...f, avec_soustitres: c }))}
                        />
                      </div>

                      {/* Description vocale - Instructions personnalisées */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Mic className="w-4 h-4" />
                          Instructions personnalisées (optionnel)
                        </Label>
                        <div className="relative">
                          <Textarea
                            value={formData.message_cle || ''}
                            onChange={e => setFormData(f => ({ ...f, message_cle: e.target.value }))}
                            placeholder="Décrivez ce que vous voulez dans la vidéo... Ex: 'Je veux un influenceur dynamique qui présente les avantages de Bureau Agricole pour les ETA'"
                            className="min-h-[100px] pr-12"
                          />
                          <VoiceInputButton 
                            onTranscript={(text) => setFormData(f => ({ 
                              ...f, 
                              message_cle: f.message_cle ? f.message_cle + ' ' + text : text 
                            }))}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          💡 Cliquez sur le micro pour dicter vos instructions vocalement
                        </p>
                      </div>

                      <Button 
                        onClick={genererScript} 
                        disabled={generatingScript}
                        className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                        size="lg"
                      >
                        {generatingScript ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération du script...</>
                        ) : (
                          <><Wand2 className="w-4 h-4 mr-2" /> Générer le script IA</>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {step === 2 && script && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <Type className="w-5 h-5" /> {script.titre}
                        </span>
                        <Badge>{script.nb_scenes} scènes</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {script.scenes?.map((scene, idx) => (
                        <div key={idx} className="p-4 border rounded-lg bg-gray-50">
                          <div className="flex items-center justify-between mb-3">
                            <Badge variant="outline">Scène {scene.numero} • {scene.duree}s</Badge>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <Label className="text-xs">Description visuelle</Label>
                              <Textarea
                                value={scene.description_visuelle}
                                onChange={e => updateScene(idx, 'description_visuelle', e.target.value)}
                                rows={2}
                                className="text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Texte voix / sous-titres</Label>
                              <Textarea
                                value={scene.texte_voix}
                                onChange={e => updateScene(idx, 'texte_voix', e.target.value)}
                                rows={2}
                                className="text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      ))}

                      <div className="flex gap-3">
                        <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                          Retour
                        </Button>
                        <Button 
                          onClick={creerProjetEtGenerer}
                          disabled={generating}
                          className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500"
                        >
                          {generating ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Lancement...</>
                          ) : (
                            <><Zap className="w-4 h-4 mr-2" /> Générer la vidéo</>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {step === 3 && projetActif && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Film className="w-5 h-5" /> Génération en cours
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="text-center py-8">
                        {projetActif.statut === 'en_cours' ? (
                          <>
                            <Loader2 className="w-16 h-16 mx-auto text-purple-500 animate-spin mb-4" />
                            <h3 className="text-xl font-semibold mb-2">Création en cours...</h3>
                            <p className="text-muted-foreground mb-4">
                              L'IA génère votre vidéo avec Sora 2
                            </p>
                            <Progress value={projetActif.progression || 10} className="max-w-md mx-auto" />
                            <p className="text-sm text-muted-foreground mt-2">
                              {projetActif.progression || 10}%
                            </p>
                          </>
                        ) : projetActif.statut === 'termine' ? (
                          <>
                            <Check className="w-16 h-16 mx-auto text-green-500 mb-4" />
                            <h3 className="text-xl font-semibold text-green-600 mb-2">Vidéo terminée !</h3>
                            <Button 
                              onClick={() => window.open(`${API_URL}/api/studio-ia/telecharger/${projetActif.id}`, '_blank')}
                              className="mt-4"
                            >
                              <Download className="w-4 h-4 mr-2" /> Télécharger
                            </Button>
                          </>
                        ) : (
                          <>
                            <X className="w-16 h-16 mx-auto text-red-500 mb-4" />
                            <h3 className="text-xl font-semibold text-red-600 mb-2">Erreur</h3>
                            <p className="text-muted-foreground">{projetActif.erreur || 'Une erreur est survenue'}</p>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Panneau latéral - Preview */}
              <div className="space-y-4">
                <Card className="bg-gradient-to-br from-purple-100 to-pink-100">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Aperçu</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className={`bg-gray-900 rounded-lg overflow-hidden mx-auto ${
                      formData.format === 'vertical_9_16' ? 'w-32 h-56' :
                      formData.format === 'carre_1_1' ? 'w-40 h-40' : 'w-full h-32'
                    }`}>
                      <div className="w-full h-full flex items-center justify-center">
                        <Video className="w-8 h-8 text-gray-600" />
                      </div>
                    </div>
                    <div className="mt-4 text-sm space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Format:</span>
                        <Badge variant="outline">{config?.formats?.find(f => f.id === formData.format)?.nom}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Durée:</span>
                        <span>{formData.duree_totale}s</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Influenceur:</span>
                        <span>{formData.influenceur_id ? 
                          (config?.influenceurs_predefinis?.find(i => i.id === formData.influenceur_id)?.nom ||
                           mesInfluenceurs.find(i => i.id === formData.influenceur_id)?.nom || 'Sélectionné')
                          : 'Non sélectionné'}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Info budget */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-amber-600">
                      <Sparkles className="w-4 h-4" />
                      <span className="text-sm font-medium">Budget IA</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Chaque vidéo utilise des crédits Sora 2. Optimisez la durée pour économiser.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* TAB: Mes influenceurs */}
          <TabsContent value="influenceurs">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Influenceurs prédéfinis */}
              <div className="col-span-full">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-500" /> Influenceurs prédéfinis
                </h3>
              </div>
              {config?.influenceurs_predefinis?.map(inf => (
                <Card key={inf.id} className="hover:shadow-md transition-all">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center text-2xl">
                        {inf.icone}
                      </div>
                      <div>
                        <h4 className="font-semibold">{inf.nom}</h4>
                        <p className="text-sm text-muted-foreground">{inf.description}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-3">
                      <Badge variant="secondary" className="text-xs">{inf.age}</Badge>
                      <Badge variant="secondary" className="text-xs">{inf.style}</Badge>
                      <Badge variant="secondary" className="text-xs">{inf.environnement}</Badge>
                    </div>
                    <Button 
                      size="sm" 
                      className="w-full"
                      onClick={() => {
                        selectInfluenceur(inf);
                        setActiveTab('creer');
                      }}
                    >
                      Utiliser
                    </Button>
                  </CardContent>
                </Card>
              ))}

              {/* Mes influenceurs personnalisés */}
              {mesInfluenceurs.length > 0 && (
                <>
                  <div className="col-span-full mt-6">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Heart className="w-4 h-4 text-pink-500" /> Mes créations
                    </h3>
                  </div>
                  {mesInfluenceurs.map(inf => (
                    <Card key={inf.id} className="hover:shadow-md transition-all border-pink-200">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-pink-100 to-purple-100 flex items-center justify-center text-2xl">
                            {inf.genre === 'femme' ? '👩' : inf.genre === 'homme' ? '👨' : '🧑'}
                          </div>
                          <div>
                            <h4 className="font-semibold">{inf.nom}</h4>
                            <p className="text-sm text-muted-foreground">
                              {inf.age} • {inf.style_vestimentaire}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1 mb-3">
                          <Badge variant="secondary" className="text-xs">{inf.environnement}</Badge>
                          <Badge variant="secondary" className="text-xs">{inf.expression}</Badge>
                        </div>
                        <Button 
                          size="sm" 
                          className="w-full bg-gradient-to-r from-pink-500 to-purple-500"
                          onClick={() => {
                            selectInfluenceur(inf);
                            setActiveTab('creer');
                          }}
                        >
                          Utiliser
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </>
              )}

              {/* Bouton créer */}
              <Card 
                className="border-2 border-dashed border-purple-300 hover:border-purple-500 hover:bg-purple-50 cursor-pointer transition-all"
                onClick={() => setShowCreateInfluenceur(true)}
              >
                <CardContent className="p-8 text-center">
                  <Plus className="w-12 h-12 mx-auto text-purple-400 mb-3" />
                  <h4 className="font-semibold text-purple-600">Créer un influenceur</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Personnalisez chaque détail
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* TAB: Mes vidéos */}
          <TabsContent value="projets">
            {projets.length === 0 ? (
              <Card className="p-12 text-center">
                <Video className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">Aucune vidéo</h3>
                <p className="text-muted-foreground mb-4">Créez votre première vidéo marketing</p>
                <Button onClick={() => setActiveTab('creer')}>
                  <Plus className="w-4 h-4 mr-2" /> Créer une vidéo
                </Button>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {projets.map(projet => (
                  <Card key={projet.id} className="overflow-hidden">
                    <div className="aspect-video bg-gray-900 relative">
                      {projet.video_url ? (
                        <video src={projet.video_url} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Film className="w-12 h-12 text-gray-600" />
                        </div>
                      )}
                      <Badge 
                        className={`absolute top-2 right-2 ${
                          projet.statut === 'termine' ? 'bg-green-500' :
                          projet.statut === 'en_cours' ? 'bg-amber-500' : 'bg-gray-500'
                        }`}
                      >
                        {projet.statut === 'termine' ? 'Terminé' :
                         projet.statut === 'en_cours' ? 'En cours' : projet.statut}
                      </Badge>
                    </div>
                    <CardContent className="p-4">
                      <h4 className="font-semibold truncate">{projet.titre || 'Vidéo sans titre'}</h4>
                      <p className="text-sm text-muted-foreground">
                        {projet.theme} • {projet.duree_totale}s
                      </p>
                      <div className="flex gap-2 mt-3">
                        {projet.statut === 'termine' && (
                          <Button 
                            size="sm" 
                            className="flex-1"
                            onClick={() => window.open(`${API_URL}/api/studio-ia/telecharger/${projet.id}`, '_blank')}
                          >
                            <Download className="w-4 h-4 mr-1" /> Télécharger
                          </Button>
                        )}
                        <Button size="sm" variant="outline">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal Créateur d'influenceur */}
      <Dialog open={showCreateInfluenceur} onOpenChange={setShowCreateInfluenceur}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <CreateurInfluenceur 
            config={config}
            onSave={onInfluenceurCreated}
            onCancel={() => setShowCreateInfluenceur(false)}
            societeId={activeSociete?.id}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudioIAMarketingPage;
